import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import "./HomePage.css"; // reuse the animated text style

export default function SubscriptionPolicyPage() {
  return (
    <Box marginTop={4}>
      <Stack
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 3, md: 5 }}
        paddingX={0}
      >
        {/* Animated main heading */}
        <Typography
          className="animate-character"
          variant={"h3"}
          fontWeight="bold"
          align="center"
          sx={{
            "@media (max-width: 600px)": {
              fontSize: "2.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 601px) and (max-width: 900px)": {
              fontSize: "3.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 901px)": {
              fontSize: "4rem",
              letterSpacing: 0.5,
            },
          }}
        >
          Subscription Policy
        </Typography>

        {/* Decorative dividers */}
        <Box sx={{ width: "100%" }}>
          <Divider />
          <Divider />
        </Box>

        {/* Content container */}
        <Box
          sx={{
            width: { xs: "95%", md: "80%" },
            maxWidth: "900px",
            textAlign: "left",
          }}
        >
          {/* Introduction */}
          <Divider sx={{ width: "100%", mb: 2, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Overview
            </Typography>
          </Divider>
          <Typography paragraph>
            SainiAudiobooks offers subscription plans that grant you access to
            our collection of Punjabi audiobooks and literary content. This
            policy explains the terms, pricing, and management of your
            subscription.
          </Typography>

          {/* Plans */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Available Plans
            </Typography>
          </Divider>
          <Typography paragraph>
            We currently offer the following subscription durations:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>1 month access</li>
            <li>6 months access</li>
            <li>12 months access</li>
          </Typography>
          <Typography paragraph>
            Each plan provides full access to all available audiobooks for the
            chosen duration. All plans are currently sold as <strong>one‑time
            purchases</strong>. You pay once and receive access for the
            specified period; there is no automatic renewal at this time.
          </Typography>

          {/* Access */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Access After Purchase
            </Typography>
          </Divider>
          <Typography paragraph>
            Upon successful payment, your subscription is activated immediately.
            You will have full access to the platform for the duration of your
            chosen plan. Access remains active until the expiry date, at which
            point it will automatically cease. No further charges will be
            incurred.
          </Typography>
          <Typography paragraph>
            You can view your subscription status and expiry date in your
            account dashboard.
          </Typography>

          {/* Renewal */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Renewal & Auto‑Renewal
            </Typography>
          </Divider>
          <Typography paragraph>
            Currently, subscriptions are <strong>manual only</strong>. After
            your subscription expires, you must purchase a new plan to regain
            access. We do not automatically charge you for renewal.
          </Typography>
          <Typography paragraph>
            Please note that we may introduce an auto‑renewal feature in the
            future. If that happens, we will provide clear notice and obtain
            your consent before enabling it.
          </Typography>

          {/* Pricing */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Pricing & Changes
            </Typography>
          </Divider>
          <Typography paragraph>
            All prices are displayed in Indian Rupees (INR) and include
            applicable taxes. We reserve the right to change our subscription
            prices at any time. However, price changes will not affect any
            existing active subscriptions; they will apply only to new
            purchases made after the change.
          </Typography>
          <Typography paragraph>
            From time to time, we may offer promotional discounts or special
            plans. These are subject to the terms stated at the time of the
            promotion.
          </Typography>

          {/* Refunds */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Refunds
            </Typography>
          </Divider>
          <Typography paragraph>
            All subscription purchases are final. Refunds will only be issued
            if a technical error on our part prevents you from accessing the
            content you purchased. In such cases, please contact us within 7
            days of the purchase date.
          </Typography>
          <Typography paragraph>
            If you wish to cancel your subscription before its expiry date, you
            may do so, but no refund will be provided for the unused portion.
          </Typography>

          {/* Account & Subscription Management */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Managing Your Subscription
            </Typography>
          </Divider>
          <Typography paragraph>
            You can view your subscription details, including the purchase date
            and expiry, by logging into your account and visiting the
            “Subscription” section. For assistance with billing or subscription
            issues, please contact our support team.
          </Typography>

          {/* Contact */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Contact Us
            </Typography>
          </Divider>
          <Typography paragraph>
            If you have any questions about this Subscription Policy or need
            help with your subscription, please reach out to us at:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              my: 2,
              p: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: 2,
              fontFamily: "monospace",
            }}
          >
            <strong>info@sainiaudiobooks.com</strong>
          </Typography>

          {/* Last updated */}
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}
          >
            Last Updated: 28 March 2026
          </Typography>
        </Box>

        {/* Back to Home button */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={{
            px: 4,
            py: 1.75,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 9999,
            background: "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
            boxShadow:
              "0 6px 18px rgba(0,114,255,0.3), 0 2px 6px rgba(0,0,0,0.1)",
            textTransform: "none",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 10px 24px rgba(0,114,255,0.4), 0 4px 10px rgba(0,0,0,0.12)",
              background:
                "linear-gradient(90deg, #0062e6 0%, #00b6ff 100%) !important",
            },
            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
          component={Link}
          to="/"
          aria-label="Return to homepage"
        >
          ← Return to Home
        </Button>

        {/* Bottom decorative dividers */}
        <Box sx={{ width: "100%", mt: 4 }}>
          <Divider />
          <Divider />
        </Box>
      </Stack>
    </Box>
  );
}